// Pure helpers of the activity log of an automation (the table and the journey of a person)

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/**
 * "Today, 9:12 AM", "Yesterday, 6:05 PM" or "Oct 18, 4:22 PM".
 * @param {string|Date|null|undefined} value
 * @param {{today: string, yesterday: string}} labels
 * @param {{now?: Date, locale?: string}} [options]
 */
export const formatWhen = (
  value,
  labels,
  { now = new Date(), locale = "en-US" } = {}
) => {
  if (!value) return "";
  const date = new Date(value);
  const time = date.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
  });
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);
  if (days === 0) return `${labels.today}, ${time}`;
  if (days === 1) return `${labels.yesterday}, ${time}`;
  const day = date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
  });
  return `${day}, ${time}`;
};

/**
 * How long until a date, as a count and a unit for the text: 2 days, 5 hours, or less than an
 * hour (count 0 hours).
 * @returns {{count: number, unit: "days"|"hours"}}
 */
export const timeLeft = (value, now = new Date()) => {
  const left = Math.max(new Date(value).getTime() - now.getTime(), 0);
  if (left >= DAY_MS) return { count: Math.round(left / DAY_MS), unit: "days" };
  return { count: Math.floor(left / HOUR_MS), unit: "hours" };
};

/** The letters of the avatar of someone with no photo. */
export const initials = (person) => {
  const source = (person?.name || person?.email || "?").trim();
  const words = source.split(/\s+/);
  return (
    words[0][0] + (words.length > 1 ? words.at(-1)[0] : "")
  ).toUpperCase();
};

export const memberHref = (userId, product) =>
  product === "press"
    ? `/coaching-portal/memberships/?customer=${userId}`
    : `/athlete-portal/profile/?athlete=${userId}&section=dashboard`;

export const chatAction = (userId) =>
  `/actions/user/check-chat-users/${userId}/press`;

/**
 * The text of the status chip: its kind (for the colour) and the key of its label. A person who
 * left at a Filter is "Exited · didn't match"; a skipped email carries its reason.
 * @returns {{kind: "sent"|"waiting"|"exited"|"skipped", label: string, reason?: string}}
 */
export const statusChip = (row) => {
  if (row.status === "exited" && row.step?.type === "filter") {
    return { kind: "exited", label: "exitedNoMatch" };
  }
  if (row.status === "skipped") {
    return { kind: "skipped", label: "skipped", reason: row.skipReason };
  }
  return { kind: row.status, label: row.status };
};

/** The link of a page of the log, keeping the filters it already has (an empty one clears it). */
export const logHref = (query, changes) => {
  const next = { ...query, ...changes };
  const params = new URLSearchParams();
  for (const key of ["tab", "status", "search", "sort", "dir", "page"]) {
    params.set(key, next[key] ?? "");
  }
  return `/actions/refresh?${params.toString()}&person=`;
};
